<template>
  <div class="voucher-edit-page w-100">

    <!-- Header -->
    <div class="w-100 px-8 py-4 bg-white border-bottom">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <div>
            <h5 class="mb-0 font-weight-bold text-dark">
              {{ isCreate ? 'Tạo voucher mới' : 'Chỉnh sửa voucher' }}
            </h5>
            <small class="text-muted" v-if="form.name">{{ form.name }}</small>
          </div>
        </div>
        <div class="d-flex gap-3">
          <b-button variant="secondary" class="mr-2" @click="goBack">Hủy</b-button>
          <loading-button :handler="handleSave" class="btn btn-success">
            {{ isCreate ? 'Tạo mới' : 'Cập nhật' }}
          </loading-button>
        </div>
      </div>
    </div>

    <!-- Body -->
    <b-overlay :show="loading">
      <div class="container py-7">
        <div class="row">

          <!-- LEFT COLUMN -->
          <div class="col-lg-7">

            <!-- Basic info -->
            <div class="form-section mb-5">
              <h6 class="section-title">Thông tin cơ bản</h6>

              <!-- Code -->
              <div class="form-group">
                <label class="form-label">
                  Mã voucher <span class="text-danger">*</span>
                  <small class="text-muted ml-1">(Ký tự in hoa, không dấu)</small>
                </label>
                <div class="d-flex gap-3">
                  <input
                    v-model="form.code"
                    v-validate="'required'"
                    name="code"
                    data-vv-as="Mã voucher"
                    type="text"
                    class="form-control code-input"
                    :class="{ 'is-invalid': errors.has('code') }"
                    @input="form.code = form.code.toUpperCase().replace(/[^A-Z0-9]/g, '')"
                  />
                </div>
                <div class="invalid-feedback d-block">{{ errors.first('code') }}</div>
              </div>

              <!-- Name -->
              <div class="form-group">
                <label class="form-label">Tên voucher <span class="text-danger">*</span></label>
                <input
                  v-model="form.name"
                  v-validate="'required'"
                  name="name"
                  data-vv-as="Tên voucher"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('name') }"
                />
                <div class="invalid-feedback">{{ errors.first('name') }}</div>
              </div>

              <div class="row">
                <div class="col-6">
                  <basic-select
                    label="Loại giảm giá"
                    placeholder="-- Chọn loại --"
                    name="discountType"
                    :options="discountTypeOptions"
                    track-by="value"
                    value-label="text"
                    :value.sync="discountTypeValue"
                    @input="form.discountType = $event"
                  />
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label class="form-label">
                      Giá trị <span class="text-danger">*</span>
                      <span class="text-muted">({{ form.discountType === 'percent' ? '%' : 'VNĐ' }})</span>
                    </label>
                    <input
                      v-model.number="form.discountValue"
                      v-validate="'required|numeric|min_value:0'"
                      name="discountValue"
                      data-vv-as="Giá trị"
                      type="number"
                      min="0"
                      class="form-control"
                      :class="{ 'is-invalid': errors.has('discountValue') }"
                      placeholder="0"
                    />
                    <div class="invalid-feedback">{{ errors.first('discountValue') }}</div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-6">
                  <div class="form-group">
                    <label class="form-label">Đơn hàng tối thiểu (VNĐ) <span class="text-danger">*</span></label>
                    <input
                      v-model.number="form.minOrderValue"
                      v-validate="'required|numeric|min_value:0'"
                      name="minOrderValue"
                      data-vv-as="Đơn hàng tối thiểu"
                      type="number"
                      min="0"
                      class="form-control"
                      :class="{ 'is-invalid': errors.has('minOrderValue') }"
                      placeholder="0"
                    />
                    <div class="invalid-feedback">{{ errors.first('minOrderValue') }}</div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="form-group">
                    <label class="form-label">
                      Giảm tối đa (VNĐ)
                      <small class="text-muted">- Tùy chọn</small>
                    </label>
                    <input
                      v-model.number="form.maxDiscount"
                      type="number"
                      min="0"
                      class="form-control"
                      :disabled="form.discountType === 'fixed'"
                    />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-6">
                  <basic-date-picker
                    label="Ngày bắt đầu"
                    placeholder="DD/MM/YYYY"
                    name="startDate"
                    data-vv-as="Ngày bắt đầu"
                    :required="true"
                    v-validate="'required'"
                    format="DD/MM/YYYY"
                    :value.sync="startDateModel"
                    :state="errors.has('startDate') ? false : null"
                    :invalidFeedback="errors.first('startDate')"
                  />
                </div>
                <div class="col-6">
                  <basic-date-picker
                    label="Ngày kết thúc"
                    placeholder="DD/MM/YYYY"
                    name="endDate"
                    data-vv-as="Ngày kết thúc"
                    :required="true"
                    v-validate="'required'"
                    format="DD/MM/YYYY"
                    :value.sync="endDateModel"
                    :state="errors.has('endDate') ? false : null"
                    :invalidFeedback="errors.first('endDate')"
                  />
                </div>
              </div>

              <div class="row">
                <div class="col-6">
                  <div class="form-group">
                    <label class="form-label">Giới hạn sử dụng <span class="text-danger">*</span></label>
                    <input
                      v-model.number="form.maxUsage"
                      v-validate="'required|numeric|min_value:1'"
                      name="maxUsage"
                      data-vv-as="Giới hạn sử dụng"
                      type="number"
                      min="1"
                      class="form-control"
                      :class="{ 'is-invalid': errors.has('maxUsage') }"
                    />
                    <div class="invalid-feedback">{{ errors.first('maxUsage') }}</div>
                  </div>
                </div>
                <div class="col-6">
                  <basic-select
                    label="Trạng thái"
                    placeholder="-- Chọn --"
                    name="status"
                    :options="statusOptions"
                    track-by="value"
                    value-label="text"
                    :value.sync="statusValue"
                    @input="form.status = $event"
                  />
                </div>
              </div>
            </div>

            <div class="form-section mb-5">
              <h6 class="section-title mb-1">Áp dụng cho gói dịch vụ</h6>
              <p class="text-muted mb-4" style="font-size:0.88rem;">
                Để trống để áp dụng cho tất cả gói. Chọn các gói cụ thể nếu muốn giới hạn.
              </p>

              <div
                class="package-row mb-2"
                :class="{ 'package-row--selected': applyAll }"
                @click="toggleApplyAll"
              >
                <b-form-checkbox :checked="applyAll" @change="toggleApplyAll" @click.native.stop />
                <div class="package-row__info">
                  <div class="package-row__name">Tất cả gói dịch vụ</div>
                  <div class="package-row__sub text-muted">Voucher áp dụng cho mọi gói</div>
                </div>
              </div>

              <div v-if="applyAll" class="apply-all-hint mb-4  text-success">
                <i class="fas fa-check mr-1"></i> Đang áp dụng cho tất cả gói dịch vụ
              </div>

              <!-- Specific packages divider -->
              <div v-if="!applyAll" class="mt-4">
                <p class="text-muted mb-3" style="font-size:0.88rem;">Hoặc chọn gói cụ thể:</p>
                <div v-if="loadingPackages" class="text-center py-4">
                  <b-spinner small /> Đang tải...
                </div>
                <div v-else>
                  <div
                    v-for="pkg in packages"
                    :key="pkg.id"
                    class="package-row mb-2"
                    :class="{
                      'package-row--selected': selectedPackageIds.includes(pkg.id),
                      'package-row--disabled': applyAll,
                    }"
                    @click="!applyAll && togglePackage(pkg.id)"
                  >
                    <b-form-checkbox
                      :checked="selectedPackageIds.includes(pkg.id)"
                      :disabled="applyAll"
                      @change="togglePackage(pkg.id)"
                      @click.native.stop
                    />
                    <div class="package-row__info">
                      <div class="package-row__name">{{ pkg.name }}</div>
                      <div class="package-row__sub text-muted">
                        {{ formatPrice(pkg.salePrice > 0 ? pkg.salePrice : pkg.price) }} VNĐ
                        <span class="mx-1">•</span>
                        {{ pkg.status === 1 ? 'Đang bán' : 'Không hoạt động' }}
                      </div>
                    </div>
                  </div>
                  <div v-if="packages.length === 0" class="text-muted text-center py-3">
                    Chưa có gói dịch vụ nào
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- RIGHT COLUMN: Preview -->
          <div class="col-lg-5">
            <div class="preview-card-wrapper">
              <div class="form-section preview-section">
                <h6 class="section-title">Xem trước Voucher</h6>
                <div class="preview-voucher">
                  <!-- Code bar -->
                  <div class="preview-code-bar">
                    {{ form.code || 'VOUCHERCODE' }}
                  </div>
                  <!-- Info -->
                  <div class="preview-body">
                    <div class="preview-name">{{ form.name || 'Tên voucher' }}</div>
                    <div class="preview-discount">
                      <template v-if="form.discountType === 'fixed'">
                        Giảm {{ formatPrice(form.discountValue) }} VNĐ
                      </template>
                      <template v-else>
                        Giảm {{ form.discountValue || 0 }}%
                      </template>
                    </div>
                    <div class="preview-min text-muted">
                      Đơn tối thiểu: {{ formatPrice(form.minOrderValue) }} VNĐ
                    </div>
                    <div class="preview-date text-muted">
                      HSD: {{ formatDatePreview(form.startDate) }} - {{ formatDatePreview(form.endDate) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
export default {
  name: 'VoucherEdit',
  data() {
    return {
      loading: false,
      loadingPackages: false,
      form: {
        id: null,
        code: '',
        name: '',
        discountType: 'percent', // 'percent' = false, 'fixed' = true
        discountValue: '',
        maxDiscount: '',
        minOrderValue: '',
        maxUsage: '',
        startDate: null,
        endDate: null,
        status: 'active',
        salePackageIds: [],
      },
      applyAll: true,
      selectedPackageIds: [],
      packages: [],
      discountTypeOptions: [
        { value: 'percent', text: 'Phần trăm (%)' },
        { value: 'fixed', text: 'Cố định (VNĐ)' },
      ],
      statusOptions: [
        { value: 'active', text: 'Hoạt động' },
        { value: 'inactive', text: 'Không hoạt động' },
      ],
    };
  },
  computed: {
    id() {
      return this.$route.params.id || null;
    },
    isCreate() {
      return !this.id;
    },

    // basic-select cần full option object, không phải primitive string
    discountTypeValue: {
      get() {
        return this.discountTypeOptions.find((o) => o.value === this.form.discountType) || null;
      },
      set(obj) {
        this.form.discountType = obj?.value ?? null;
      },
    },
    statusValue: {
      get() {
        return this.statusOptions.find((o) => o.value === this.form.status) || null;
      },
      set(obj) {
        this.form.status = obj?.value ?? null;
      },
    },

    // basic-date-picker với format="DD/MM/YYYY" cần giá trị dạng DD/MM/YYYY
    startDateModel: {
      get() {
        return this.toDisplayDate(this.form.startDate);
      },
      set(val) {
        this.form.startDate = this.fromDisplayDate(val);
      },
    },
    endDateModel: {
      get() {
        return this.toDisplayDate(this.form.endDate);
      },
      set(val) {
        this.form.endDate = this.fromDisplayDate(val);
      },
    },
  },
  async created() {
    await this.loadPackages();
    if (!this.isCreate) {
      await this.loadVoucher();
    }
  },
  methods: {
    // Chuyển YYYY-MM-DD hoặc timestamp số sang DD/MM/YYYY (basic-date-picker format)
    toDisplayDate(val) {
      if (!val && val !== 0) return null;
      // Timestamp số giây
      if (typeof val === 'number') {
        const ms = val < 1e10 ? val * 1000 : val;
        const d = new Date(ms);
        if (isNaN(d.getTime())) return null;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${dd}/${mm}/${d.getFullYear()}`;
      }
      // YYYY-MM-DD string
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const [y, m, d] = val.split('-');
        return `${d}/${m}/${y}`;
      }
      // DD/MM/YYYY đã đúng format
      if (typeof val === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
        return val;
      }
      return null;
    },

    // Chuyển DD/MM/YYYY (từ basic-date-picker) sang YYYY-MM-DD để lưu và gửi API
    fromDisplayDate(val) {
      if (!val) return null;
      if (typeof val === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
        const [d, m, y] = val.split('/');
        return `${y}-${m}-${d}`;
      }
      return val; // YYYY-MM-DD falls through as-is
    },

    normalizeDateForPicker(val, { emptyToNull = false } = {}) {
      if (val === null || val === undefined || val === '') return emptyToNull ? null : '';
      if (val instanceof Date) {
        if (Number.isNaN(val.getTime())) return emptyToNull ? null : '';
        const yyyy = val.getFullYear();
        const mm = String(val.getMonth() + 1).padStart(2, '0');
        const dd = String(val.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

      let timestamp = Number(val);
      if (Number.isNaN(timestamp)) return emptyToNull ? null : '';
      if (timestamp < 1e10) timestamp *= 1000; // seconds -> ms

      const d = new Date(timestamp);
      if (Number.isNaN(d.getTime())) return emptyToNull ? null : '';
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    async loadVoucher() {
      try {
        this.loading = true;
        const res = await this.$api.get(`/BundleVouchers/${this.id}`);
        // Xác định đúng object voucher từ response (hỗ trợ cả nested và flat)
        let raw = res?.data ?? res;
        // Nếu có dạng { meta, data: {...} } thì lấy raw.data
        if (raw && raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
          raw = raw.data;
        }
        this.mapToForm(raw);
      } catch (e) {
        this.$toastr?.e({ title: 'Lỗi', msg: 'Không thể tải thông tin voucher' });
        this.goBack();
      } finally {
        this.loading = false;
      }
    },

 mapToForm(data) {
  const discountTypeStr = data.discountType === true ? 'fixed' : 'percent';

  this.form = {
    id:            data.id ?? null,
    code:          data.code ?? '',
    name:          data.title ?? data.name ?? '',
    discountType:  discountTypeStr,
    discountValue: data.discountValue ?? 0,
    maxDiscount:   data.maxDiscountAmount ?? 0,
    minOrderValue: data.minOrderAmount   ?? 0,
    maxUsage:      data.usageLimit       ?? 100,
    startDate:     this.normalizeDateForPicker(data.startDate, { emptyToNull: true }),
    endDate:       this.normalizeDateForPicker(data.endDate, { emptyToNull: true }),
    status:        data.status === true ? 'active' : 'inactive',
    salePackageIds: [],
  };

  this.applyAll = !data.scopeType;
  this.selectedPackageIds = (data.salePackages ?? []).map(sp => sp.salePackageId).filter(Boolean);
},
    async loadPackages() {
      try {
        this.loadingPackages = true;
        const res = await this.$api.get('SalePackage', { params: new URLSearchParams({ page: 1, size: 100 }) });
        // SalePackage API shape: { meta, data: { items: [...] } }
        const raw = res?.data ?? res;
        const normalized =
          raw?.data?.items ? raw.data.items :
          raw?.data?.data?.items ? raw.data.data.items :
          raw?.items ? raw.items :
          raw?.data?.items ? raw.data.items :
          raw;

        this.packages = Array.isArray(normalized)
          ? normalized
          : Array.isArray(normalized?.items)
            ? normalized.items
            : [];
      } catch (e) {
        console.error('Không thể tải danh sách gói:', e);
      } finally {
        this.loadingPackages = false;
      }
    },

    toggleApplyAll() {
      this.applyAll = !this.applyAll;
      if (this.applyAll) this.selectedPackageIds = [];
    },

    togglePackage(id) {
      const idx = this.selectedPackageIds.indexOf(id);
      if (idx === -1) {
        this.selectedPackageIds = [...this.selectedPackageIds, id];
      } else {
        this.selectedPackageIds = this.selectedPackageIds.filter((x) => x !== id);
      }
    },

 

    async handleSave() {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        this.$toastr?.e({ title: 'Lỗi!', msg: 'Vui lòng điền đầy đủ thông tin bắt buộc!' });
        return;
      }

      const formData = new FormData();
      formData.append('code', this.form.code);
      formData.append('title', this.form.name);
      formData.append('discountType', this.form.discountType === 'fixed');
      formData.append('discountValue', Number(this.form.discountValue) || 0);
      formData.append('minOrderAmount', Number(this.form.minOrderValue) || 0);
      if(this.form.discountType === 'percent') {
        formData.append('maxDiscountAmount', Number(this.form.maxDiscount) || 0);
      }
      formData.append('usageLimit', Number(this.form.maxUsage) || 100);
      
      // Normalize và convert sang epoch (xử lý cả DD/MM/YYYY và YYYY-MM-DD)
      const parseToEpoch = (val) => {
        if (!val) return 0;
        let normalized = val;
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
          const [d, m, y] = val.split('/');
          normalized = `${y}-${m}-${d}`;
        }
        const ms = new Date(normalized).getTime();
        return isNaN(ms) ? 0 : Math.floor(ms / 1000);
      };

      const startTimestamp = parseToEpoch(this.form.startDate);
      const endTimestamp = parseToEpoch(this.form.endDate);
      
      formData.append('startDate', startTimestamp);
      formData.append('endDate', endTimestamp);
      formData.append('scopeType', !this.applyAll);
      formData.append('status', this.form.status === 'active');

      const salePackageIds = this.applyAll ? [] : [...this.selectedPackageIds];
      formData.append('salePackageIds', salePackageIds.join(','));

      try {
        this.loading = true;
        if (this.isCreate) {
          await this.$api.post('/BundleVouchers', formData);
          this.$toastr?.s({ title: 'Thành công!', msg: 'Tạo voucher thành công!' });
        } else {
          await this.$api.put(`/BundleVouchers/${this.id}`, formData);
          this.$toastr?.s({ title: 'Thành công!', msg: 'Cập nhật voucher thành công!' });
        }
        this.goBack();
      } catch (e) {
        this.$toastr?.e({ title: 'Lỗi!', msg: e?.message || 'Có lỗi xảy ra' });
      } finally {
        this.loading = false;
      }
    },

    formatPrice(val) {
      if (!val && val !== 0) return '0';
      return new Intl.NumberFormat('vi-VN').format(val);
    },

    formatDatePreview(val) {
      if (!val) return 'DD/MM/YYYY';
      return this.$moment(val).format('DD/MM/YYYY');
    },

    goBack() {
      this.$router.push({ name: 'clinic_voucher' });
    },
  },
};
</script>

<style scoped lang="scss">
.voucher-edit-page {
  background: #f8f9fa;
  min-height: 100vh;
}

.form-section {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 12px;
  padding: 24px 28px;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #181c32;
  margin-bottom: 18px;
}

.form-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: #3f4254;
  margin-bottom: 6px;
}

.code-input {
  letter-spacing: 1px;
  font-weight: 600;
  text-transform: uppercase;
}

.package-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(.package-row--disabled) {
    background: #f5f8fa;
  }

  &--selected {
    border-color: #00a63e;
    background: #dcfce7;
  }

  &--disabled {
    opacity: 0.5;
    cursor: default;
  }

  &__info {
    flex: 1;
  }

  &__name {
    font-weight: 600;
    font-size: 0.93rem;
    color: #181c32;
  }

  &__sub {
    font-size: 0.82rem;
    margin-top: 2px;
  }
}

.apply-all-hint {
  border-radius: 8px;
  background-color: #dcfce7;
  padding: 10px 16px;
  font-size: 0.85rem;
  font-weight: 500;
}

.preview-card-wrapper {
  position: sticky;
  top: 24px;
}

.preview-section {
  background: linear-gradient(135deg, #f0f4ff 0%, #f5f0ff 100%);
  border: 1px solid #d6e0ff;
}

.preview-voucher {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(76, 87, 125, 0.1);
}

.preview-code-bar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-align: center;
  padding: 16px 20px;
}

.preview-body {
  padding: 16px 18px;
}

.preview-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: #181c32;
  margin-bottom: 6px;
}

.preview-discount {
  font-size: 1.4rem;
  font-weight: 700;
  color: #17c653;
  margin-bottom: 6px;
}

.preview-min,
.preview-date {
  font-size: 0.82rem;
  margin-top: 4px;
}
</style>